import { useState, useEffect, useCallback } from "react";

// ============================================================
//  Mobile-Responsive Sentinel UI
// ============================================================

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

function api(path, opts = {}) {
  const token = localStorage.getItem("sentinel_token");
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body,
  }).then(r => r.json());
}

const C = {
  bg:        "#07090d",
  surface:   "#0f1117",
  surfaceAlt:"#141820",
  border:    "#1c2233",
  borderHi:  "#2a3550",
  accent:    "#00e5b4",
  accentDim: "#00e5b415",
  warn:      "#ff6b35",
  critical:  "#ff3366",
  gold:      "#f5c842",
  green:     "#00e5b4",
  text:      "#dde6f0",
  textMuted: "#5a6a82",
  textDim:   "#8fa3bc",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{font-size:16px}
  body{background:${C.bg};color:${C.text};font-family:'Space Grotesk',sans-serif;overflow-x:hidden}
  ::-webkit-scrollbar{width:3px}
  ::-webkit-scrollbar-track{background:${C.surface}}
  ::-webkit-scrollbar-thumb{background:${C.borderHi};border-radius:2px}
  input,select,textarea{background:${C.surfaceAlt};border:1px solid ${C.border};border-radius:6px;color:${C.text};font-family:'Space Grotesk',sans-serif;font-size:14px;padding:10px 13px;outline:none;transition:border-color .2s;width:100%}
  input:focus,select:focus{border-color:${C.accent}}
  input::placeholder{color:${C.textMuted}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
  .fade{animation:fadeUp .3s ease both}
  .mono{font-family:'DM Mono',monospace}
`;

function useBreakpoint() {
  const [bp, setBp] = useState("desktop");
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setBp(w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop");
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return bp;
}

const isMob = bp => bp === "mobile";
const isTab = bp => bp === "tablet" || bp === "mobile";
const sevCol = s => ({critical:C.critical,high:C.warn,medium:C.gold,low:C.accent})[s]||C.textMuted;

function Pill({ children, color=C.accent }) {
  return (
    <span style={{ background:color+"18",color,border:`1px solid ${color}35`,borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700,fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:1,whiteSpace:"nowrap" }}>
      {children}
    </span>
  );
}

function Btn({ children, variant="primary", onClick, disabled, loading, full, style={}, size="md" }) {
  const pad  = size==="sm"?"6px 14px":size==="lg"?"13px 28px":"9px 20px";
  const fs   = size==="sm"?12:size==="lg"?15:13;
  const vars = {
    primary:{ background:C.accent,   color:C.bg,       border:"none" },
    outline:{ background:"transparent",color:C.accent, border:`1px solid ${C.accent}` },
    ghost:  { background:"transparent",color:C.textDim, border:`1px solid ${C.border}` },
    danger: { background:C.critical+"18",color:C.critical,border:`1px solid ${C.critical}40` },
  };
  return (
    <button onClick={!disabled&&!loading?onClick:undefined} style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,padding:pad,borderRadius:6,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,fontSize:fs,cursor:disabled||loading?"not-allowed":"pointer",opacity:disabled||loading?.5:1,transition:"all .15s",width:full?"100%":"auto",...vars[variant],...style }}>
      {loading&&<div style={{ width:fs,height:fs,border:"2px solid transparent",borderTopColor:"currentColor",borderRadius:"50%",animation:"spin .65s linear infinite" }} />}
      {children}
    </button>
  );
}

function Card({ children, style={} }) {
  return <div style={{ background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,...style }}>{children}</div>;
}

function Spinner() {
  return <div style={{ display:"flex",justifyContent:"center",padding:40 }}><div style={{ width:28,height:28,border:`3px solid ${C.border}`,borderTopColor:C.accent,borderRadius:"50%",animation:"spin .7s linear infinite" }} /></div>;
}

const NAV_ITEMS = [
  { id:"dashboard",       icon:"▦", label:"Home"    },
  { id:"targets",         icon:"⊞", label:"Targets" },
  { id:"scanner",         icon:"◎", label:"Scan"    },
  { id:"vulnerabilities", icon:"⚠", label:"Vulns"   },
  { id:"reports",         icon:"⊟", label:"Reports" },
];

function BottomNav({ page, onNav }) {
  return (
    <div style={{ position:"fixed",bottom:0,left:0,right:0,background:C.surface,borderTop:`1px solid ${C.border}`,display:"flex",zIndex:100,paddingBottom:"env(safe-area-inset-bottom)" }}>
      {NAV_ITEMS.map(item => (
        <button key={item.id} onClick={() => onNav(item.id)} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"10px 4px",background:"transparent",border:"none",color:page===item.id?C.accent:C.textMuted,cursor:"pointer",fontSize:11,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,transition:"color .15s",position:"relative" }}>
          {page===item.id && <div style={{ position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:24,height:2,background:C.accent,borderRadius:"0 0 2px 2px" }} />}
          <span style={{ fontSize:18 }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
}

const FULL_NAV = [
  { id:"dashboard",       icon:"▦", label:"Dashboard"     },
  { id:"scanner",         icon:"◎", label:"New Scan"      },
  { id:"targets",         icon:"⊞", label:"Targets"       },
  { id:"vulnerabilities", icon:"⚠", label:"Vulnerabilities" },
  { id:"reports",         icon:"⊟", label:"Reports"       },
  { id:"billing",         icon:"◈", label:"Billing"       },
  { id:"settings",        icon:"⊙", label:"Settings"      },
];

function Sidebar({ page, onNav, user, onLogout, collapsed }) {
  const w = collapsed ? 64 : 220;
  return (
    <div style={{ width:w,background:C.surface,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",position:"fixed",height:"100vh",zIndex:20,transition:"width .25s ease",overflow:"hidden" }}>
      <div style={{ padding:collapsed?"14px 0":"20px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10,justifyContent:collapsed?"center":"flex-start" }}>
        <span style={{ fontSize:20,color:C.accent,flexShrink:0 }}>⬡</span>
        {!collapsed && <div>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:14,fontWeight:500,letterSpacing:1,color:C.accent }}>SENTINEL</div>
          <div style={{ fontSize:9,color:C.textMuted,letterSpacing:2 }}>PENTEST</div>
        </div>}
      </div>
      <nav style={{ flex:1,padding:collapsed?"8px 4px":"10px 8px",overflowY:"auto" }}>
        {FULL_NAV.map(item=>(
          <button key={item.id} onClick={()=>onNav(item.id)} title={collapsed?item.label:undefined} style={{ width:"100%",display:"flex",alignItems:"center",gap:collapsed?0:10,padding:collapsed?"10px":"9px 11px",background:page===item.id?C.accentDim:"transparent",border:`1px solid ${page===item.id?C.accent+"25":"transparent"}`,borderRadius:6,color:page===item.id?C.accent:C.textMuted,cursor:"pointer",fontSize:13,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,marginBottom:2,transition:"all .15s",textAlign:"left",justifyContent:collapsed?"center":"flex-start" }}>
            <span style={{ fontSize:15,flexShrink:0 }}>{item.icon}</span>
            {!collapsed && item.label}
          </button>
        ))}
      </nav>
      {!collapsed && (
        <div style={{ padding:"10px 8px",borderTop:`1px solid ${C.border}` }}>
          <div style={{ background:C.surfaceAlt,borderRadius:7,padding:"9px 11px",marginBottom:8 }}>
            <div style={{ fontSize:12,fontWeight:600,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user?.full_name||user?.email}</div>
            <Pill color={C.accent}>{user?.plan_name||"Trial"}</Pill>
          </div>
          <Btn variant="ghost" size="sm" full onClick={onLogout}>Sign Out</Btn>
        </div>
      )}
    </div>
  );
}

function MobileHeader({ page, user, onMenuOpen }) {
  const label = FULL_NAV.find(n=>n.id===page)?.label || "Dashboard";
  return (
    <div style={{ position:"fixed",top:0,left:0,right:0,height:56,background:C.surface,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",padding:"0 16px",zIndex:100,gap:12 }}>
      <span style={{ fontSize:18,color:C.accent }}>⬡</span>
      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:13,letterSpacing:1,color:C.accent,flex:1 }}>SENTINEL</div>
      <div style={{ fontSize:13,color:C.textMuted }}>{label}</div>
      <button onClick={onMenuOpen} style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:5,color:C.textDim,padding:"5px 9px",cursor:"pointer",fontSize:16 }}>☰</button>
    </div>
  );
}

function MobileDrawer({ open, onClose, page, onNav, user, onLogout }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position:"fixed",inset:0,background:"#00000070",zIndex:200 }} />
      <div style={{ position:"fixed",top:0,right:0,bottom:0,width:260,background:C.surface,borderLeft:`1px solid ${C.border}`,zIndex:201,display:"flex",flexDirection:"column",animation:"slideDown .25s ease" }}>
        <div style={{ padding:"20px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:14,color:C.accent,letterSpacing:1 }}>SENTINEL</div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:C.textMuted,fontSize:22,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:"10px 10px",flex:1,overflowY:"auto" }}>
          {FULL_NAV.map(item=>(
            <button key={item.id} onClick={()=>{onNav(item.id);onClose();}} style={{ width:"100%",display:"flex",alignItems:"center",gap:12,padding:"11px 13px",background:page===item.id?C.accentDim:"transparent",border:`1px solid ${page===item.id?C.accent+"25":"transparent"}`,borderRadius:7,color:page===item.id?C.accent:C.textDim,cursor:"pointer",fontSize:14,fontFamily:"'Space Grotesk',sans-serif",fontWeight:600,marginBottom:3,transition:"all .15s",textAlign:"left" }}>
              <span style={{ fontSize:16 }}>{item.icon}</span>{item.label}
            </button>
          ))}
        </div>
        <div style={{ padding:"12px 10px",borderTop:`1px solid ${C.border}` }}>
          <div style={{ background:C.surfaceAlt,borderRadius:7,padding:"10px 12px",marginBottom:10 }}>
            <div style={{ fontSize:13,fontWeight:600,marginBottom:3 }}>{user?.full_name||user?.email}</div>
            <Pill color={C.accent}>{user?.plan_name||"Trial"}</Pill>
          </div>
          <Btn variant="ghost" full onClick={()=>{onLogout();onClose();}}>Sign Out</Btn>
        </div>
      </div>
    </>
  );
}

// ── Dashboard ──────────────────────────────────────────────
function Dashboard({ bp, onNav }) {
  const mob = isMob(bp);
  const cols = mob ? 2 : 4;
  const [loading, setLoading] = useState(true);
  const [stats,   setStats]   = useState({ critical:0, high:0, issues:0, assets:0 });
  const [targets, setTargets] = useState([]);

  useEffect(() => {
    Promise.all([api("/api/targets"), api("/api/vulnerabilities")]).then(([tgts, vulns]) => {
      const t = Array.isArray(tgts) ? tgts : [];
      const v = Array.isArray(vulns) ? vulns : [];
      setStats({
        critical: v.filter(x=>x.severity==="critical").length,
        high:     v.filter(x=>x.severity==="high").length,
        issues:   v.length,
        assets:   t.length,
      });
      setTargets(t.slice(0,5));
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  const statsArr = [
    { label:"Critical", val:stats.critical, color:C.critical, icon:"☠" },
    { label:"High",     val:stats.high,     color:C.warn,     icon:"⚡" },
    { label:"Issues",   val:stats.issues,   color:C.gold,     icon:"◎" },
    { label:"Assets",   val:stats.assets,   color:C.accent,   icon:"⬡" },
  ];

  if (loading) return <Spinner />;

  return (
    <div className="fade">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Security Overview</h1>
        <div style={{ fontSize:12,color:C.textMuted }}>{targets.length} targets monitored</div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:mob?10:14,marginBottom:20 }}>
        {statsArr.map((s,i) => (
          <Card key={i} style={{ padding:mob?14:18,position:"relative",overflow:"hidden" }}>
            <div style={{ position:"absolute",top:10,right:10,fontSize:mob?16:22,opacity:.1,color:s.color }}>{s.icon}</div>
            <div style={{ fontSize:mob?28:36,fontWeight:900,color:s.color,fontFamily:"'DM Mono',monospace",marginBottom:2 }}>{s.val}</div>
            <div style={{ fontSize:mob?10:12,color:C.textMuted,fontWeight:600 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {mob && (
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20 }}>
          <Btn variant="primary" full onClick={()=>onNav("scanner")}>+ New Scan</Btn>
          <Btn variant="ghost"   full onClick={()=>onNav("reports")}>Reports</Btn>
        </div>
      )}

      <Card style={{ marginBottom:16,overflow:"hidden" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:mob?"12px 14px":"14px 18px",borderBottom:`1px solid ${C.border}` }}>
          <div style={{ fontWeight:700,fontSize:mob?13:15 }}>Targets</div>
          {!mob && <Btn variant="primary" size="sm" onClick={()=>onNav("scanner")}>+ New Scan</Btn>}
        </div>
        {targets.length === 0 ? (
          <div style={{ padding:28,textAlign:"center",color:C.textMuted,fontSize:13 }}>
            No targets yet. <span style={{ color:C.accent,cursor:"pointer" }} onClick={()=>onNav("targets")}>Add your first target →</span>
          </div>
        ) : targets.map((t,i) => (
          <div key={t.id||i} style={{ display:"flex",alignItems:"center",gap:mob?10:14,padding:mob?"11px 14px":"12px 18px",borderBottom:`1px solid ${C.border}22` }}>
            <div style={{ flex:1,minWidth:0 }}>
              <div style={{ fontWeight:600,fontSize:mob?13:14,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.name}</div>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:11,color:C.textMuted }}>{t.ip_address||t.hostname||"—"}</div>
            </div>
            <Pill color={C.textMuted}>Active</Pill>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── Scanner ────────────────────────────────────────────────
function Scanner({ bp }) {
  const mob = isMob(bp);
  const [targets,  setTargets]  = useState([]);
  const [targetId, setTargetId] = useState("");
  const [scanType, setScanType] = useState("full");
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [log,      setLog]      = useState([]);
  const [done,     setDone]     = useState(false);
  const [err,      setErr]      = useState("");
  const [scanId,   setScanId]   = useState(null);

  useEffect(() => {
    api("/api/targets").then(d => {
      const t = Array.isArray(d) ? d : [];
      setTargets(t);
      if (t.length > 0) setTargetId(t[0].id);
    });
  }, []);

  const launch = async () => {
    if (!targetId) return;
    setErr(""); setScanning(true); setProgress(0); setLog([]); setDone(false); setScanId(null);
    const tgt = targets.find(t => t.id === targetId);
    setLog([
      "Initialising scan engine...",
      `Target: ${tgt?.name || targetId}`,
      `Scan type: ${scanType.toUpperCase()}`,
      "Queuing scan on server...",
    ]);
    try {
      const res = await api("/api/scans", { method:"POST", body: JSON.stringify({ target_id: targetId, scan_type: scanType }) });
      if (res.id) {
        setScanId(res.id);
        setProgress(5);
        setLog(p => [...p, `Scan queued (${res.id.slice(0,8)})`, "Nmap running — this may take a few minutes..."]);
      } else {
        setErr(res.error || "Failed to start scan");
        setScanning(false);
      }
    } catch(e) {
      setErr("Failed to connect to scanner");
      setScanning(false);
    }
  };

  // Poll scan status every 4 seconds
  useEffect(() => {
    if (!scanId || done) return;
    const iv = setInterval(async () => {
      try {
        const scan = await api(`/api/scans/${scanId}`);
        if (scan.status === "completed") {
          clearInterval(iv);
          setProgress(100);
          setScanning(false);
          setDone(true);
          const lines = [
            `Scan complete — duration: ${scan.scan_duration || "?"}s`,
            `Open ports detected: ${scan.open_ports || 0}`,
            `Vulnerabilities found: ${scan.total_vulns || 0}`,
          ];
          if (scan.critical_count > 0) lines.push(`[CRITICAL] ${scan.critical_count} critical vulnerabilit${scan.critical_count===1?"y":"ies"} detected!`);
          if (scan.high_count     > 0) lines.push(`[HIGH]     ${scan.high_count} high vulnerabilit${scan.high_count===1?"y":"ies"} detected`);
          lines.push(`Risk score: ${scan.risk_score || 0}/10`);
          lines.push("Done. Check Vulnerabilities tab for full details.");
          setLog(p => [...p, ...lines]);
        } else if (scan.status === "failed") {
          clearInterval(iv);
          setScanning(false);
          setErr("Scan failed — check that the target is reachable.");
        } else {
          setProgress(p => Math.min(p + 6, 88));
        }
      } catch(e) { /* ignore transient errors */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [scanId, done]);

  const scanTypes = [
    {id:"quick",  icon:"⚡", label:"Quick",   sub:"~2 min"},
    {id:"full",   icon:"◎",  label:"Full",    sub:"~15 min"},
    {id:"vuln",   icon:"⚠",  label:"Vuln",    sub:"~20 min"},
    {id:"stealth",icon:"▦",  label:"Stealth", sub:"~10 min"},
  ];

  return (
    <div className="fade">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>New Scan</h1>
        <div style={{ fontSize:12,color:C.textMuted }}>Authorised systems only</div>
      </div>

      <Card style={{ padding:mob?14:20,marginBottom:14 }}>
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>Select Target</label>
          {targets.length === 0 ? (
            <div style={{ padding:"10px 13px",background:C.surfaceAlt,border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.textMuted }}>
              No targets yet — add one in the Targets tab first.
            </div>
          ) : (
            <select value={targetId} onChange={e=>setTargetId(e.target.value)}>
              {targets.map(t=><option key={t.id} value={t.id}>{t.name} ({t.ip_address||t.hostname||"no address"})</option>)}
            </select>
          )}
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:8 }}>Scan Type</div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
            {scanTypes.map(s=>(
              <button key={s.id} onClick={()=>setScanType(s.id)} style={{ background:scanType===s.id?C.accentDim:C.surfaceAlt,border:`1.5px solid ${scanType===s.id?C.accent:C.border}`,borderRadius:7,padding:"10px 12px",cursor:"pointer",textAlign:"left",transition:"all .15s" }}>
                <div style={{ fontSize:mob?14:16,marginBottom:3,color:scanType===s.id?C.accent:C.text }}>{s.icon}</div>
                <div style={{ fontSize:12,fontWeight:700,color:scanType===s.id?C.accent:C.text,marginBottom:1 }}>{s.label}</div>
                <div style={{ fontSize:10,color:C.textMuted }}>{s.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {err && <div style={{ fontSize:12,color:C.critical,marginBottom:10 }}>⚠ {err}</div>}
        <Btn variant={!targetId||scanning?"ghost":"primary"} full onClick={launch} disabled={!targetId||targets.length===0||scanning} loading={scanning}>
          {scanning?"Scanning...":"⊕ Launch Scan"}
        </Btn>
      </Card>

      {(log.length>0||scanning) && (
        <Card style={{ overflow:"hidden" }}>
          <div style={{ background:C.surfaceAlt,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <div style={{ display:"flex",gap:5 }}>{["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{ width:9,height:9,borderRadius:"50%",background:c }} />)}</div>
            {scanning&&<span style={{ fontFamily:"'DM Mono',monospace",fontSize:10,color:C.accent,animation:"pulse 1s infinite" }}>● SCANNING</span>}
            {done&&<span style={{ fontFamily:"'DM Mono',monospace",fontSize:10,color:C.accent }}>● DONE</span>}
          </div>
          {progress>0&&(
            <div style={{ padding:"8px 14px",borderBottom:`1px solid ${C.border}`,background:C.surfaceAlt }}>
              <div style={{ height:3,background:C.border,borderRadius:2 }}>
                <div style={{ height:"100%",width:`${progress}%`,background:C.accent,borderRadius:2,transition:"width .4s" }} />
              </div>
            </div>
          )}
          <div style={{ padding:14,maxHeight:mob?200:280,overflowY:"auto",fontFamily:"'DM Mono',monospace",fontSize:mob?11:12,lineHeight:1.9 }}>
            {log.map((l,i)=> typeof l !== "string" ? null : (
              <div key={i} style={{ color:l.includes("[CRITICAL]")?C.critical:l.includes("[HIGH]")?C.warn:l.includes("open")||l.includes("Done")?C.accent:C.textDim }}>
                {l}
              </div>
            ))}
            {scanning&&<span style={{ color:C.accent,animation:"pulse 1s infinite" }}>▋</span>}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Targets ────────────────────────────────────────────────
function Targets({ bp, onNav }) {
  const mob = isMob(bp);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form,    setForm]    = useState({ name:"", ip_address:"", hostname:"", description:"", authorized_by:"", auth_date:new Date().toISOString().split("T")[0] });
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");

  const load = useCallback(() => {
    api("/api/targets").then(d => { setTargets(Array.isArray(d)?d:[]); setLoading(false); });
  }, []);

  useEffect(()=>{ load(); },[load]);

  const addTarget = async () => {
    if (!form.name || !form.authorized_by) { setErr("Name and Authorised By are required"); return; }
    setSaving(true); setErr("");
    const res = await api("/api/targets", { method:"POST", body: JSON.stringify(form) });
    setSaving(false);
    if (res.id) { setTargets(p=>[res,...p]); setShowAdd(false); setForm({ name:"",ip_address:"",hostname:"",description:"",authorized_by:"",auth_date:new Date().toISOString().split("T")[0] }); }
    else setErr(res.error || "Failed to add target");
  };

  const deleteTarget = async (id) => {
    await api(`/api/targets/${id}`, { method:"DELETE" });
    setTargets(p=>p.filter(t=>t.id!==id));
  };

  if (loading) return <Spinner />;

  return (
    <div className="fade">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Targets</h1>
          <div style={{ fontSize:12,color:C.textMuted }}>{targets.length} authorised targets</div>
        </div>
        <Btn variant="primary" size={mob?"sm":"md"} onClick={()=>setShowAdd(p=>!p)}>+ Add</Btn>
      </div>

      {showAdd && (
        <Card style={{ padding:mob?14:20,marginBottom:16,border:`1px solid ${C.accent}30` }}>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:14,color:C.accent }}>Add New Target</div>
          <div style={{ display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:"0 16px" }}>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>Name *</label>
              <input placeholder="e.g. Production Web Server" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>IP Address</label>
              <input placeholder="192.168.1.1" value={form.ip_address} onChange={e=>setForm(p=>({...p,ip_address:e.target.value}))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>Hostname</label>
              <input placeholder="server.company.co.za" value={form.hostname} onChange={e=>setForm(p=>({...p,hostname:e.target.value}))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>Authorised By *</label>
              <input placeholder="John Smith (CTO)" value={form.authorized_by} onChange={e=>setForm(p=>({...p,authorized_by:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>Description</label>
            <input placeholder="Optional description" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} />
          </div>
          {err && <div style={{ fontSize:12,color:C.critical,marginBottom:10 }}>⚠ {err}</div>}
          <div style={{ display:"flex",gap:8 }}>
            <Btn variant="primary" onClick={addTarget} loading={saving}>Save Target</Btn>
            <Btn variant="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {targets.length === 0 ? (
        <Card style={{ padding:40,textAlign:"center" }}>
          <div style={{ fontSize:36,marginBottom:12 }}>⊞</div>
          <div style={{ fontWeight:700,fontSize:16,marginBottom:8 }}>No targets yet</div>
          <div style={{ fontSize:13,color:C.textMuted,marginBottom:20 }}>Add a system you're authorised to test.</div>
          <Btn variant="primary" onClick={()=>setShowAdd(true)}>+ Add First Target</Btn>
        </Card>
      ) : (
        <div style={{ display:"grid",gridTemplateColumns:mob?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:12 }}>
          {targets.map(t => (
            <Card key={t.id} style={{ padding:mob?14:18 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                <div style={{ fontWeight:700,fontSize:mob?14:15,flex:1,marginRight:8 }}>{t.name}</div>
                <button onClick={()=>deleteTarget(t.id)} style={{ background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:16,padding:0,lineHeight:1 }}>×</button>
              </div>
              {(t.ip_address||t.hostname) && <div style={{ fontFamily:"'DM Mono',monospace",fontSize:12,color:C.accent,marginBottom:3 }}>{t.ip_address||t.hostname}</div>}
              {t.description && <div style={{ fontSize:12,color:C.textMuted,marginBottom:10 }}>{t.description}</div>}
              <div style={{ display:"flex",justifyContent:"space-between",borderTop:`1px solid ${C.border}`,paddingTop:10,alignItems:"center" }}>
                <div style={{ fontSize:11,color:C.textMuted }}>Auth: {t.authorized_by}</div>
                <Btn variant="outline" size="sm" onClick={()=>onNav("scanner")}>Scan</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Vulnerabilities ────────────────────────────────────────
function Vulns({ bp }) {
  const mob = isMob(bp);
  const [filter,  setFilter]  = useState("all");
  const [vulns,   setVulns]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/api/vulnerabilities").then(d => { setVulns(Array.isArray(d)?d:[]); setLoading(false); });
  }, []);

  const updateStatus = async (id, status) => {
    await api(`/api/vulnerabilities/${id}`, { method:"PATCH", body: JSON.stringify({ status }) });
    setVulns(p=>p.map(v=>v.id===id?{...v,status}:v));
  };

  const shown = filter==="all" ? vulns : vulns.filter(v=>v.severity===filter);

  if (loading) return <Spinner />;

  return (
    <div className="fade">
      <div style={{ marginBottom:16 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Vulnerabilities</h1>
        <div style={{ fontSize:12,color:C.textMuted }}>{shown.length} issues{filter!=="all"?` (${filter})`:" found"}</div>
      </div>

      <div style={{ display:"flex",gap:6,marginBottom:16,overflowX:"auto",paddingBottom:4 }}>
        {["all","critical","high","medium","low"].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{ background:filter===s?(s==="all"?C.accent:sevCol(s))+"22":"transparent",color:filter===s?(s==="all"?C.accent:sevCol(s)):C.textMuted,border:`1px solid ${filter===s?(s==="all"?C.accent:sevCol(s)):C.border}`,borderRadius:4,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'DM Mono',monospace",textTransform:"uppercase",whiteSpace:"nowrap",transition:"all .15s" }}>{s}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card style={{ padding:40,textAlign:"center" }}>
          <div style={{ fontSize:36,marginBottom:12 }}>✓</div>
          <div style={{ fontWeight:700,fontSize:16,marginBottom:8,color:C.accent }}>No vulnerabilities found</div>
          <div style={{ fontSize:13,color:C.textMuted }}>Run a scan to detect security issues.</div>
        </Card>
      ) : (
        <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
          {shown.map(v => (
            <Card key={v.id} style={{ padding:mob?12:16,borderLeft:`3px solid ${sevCol(v.severity)}` }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6 }}>
                <div style={{ flex:1,marginRight:12 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap" }}>
                    <Pill color={sevCol(v.severity)}>{v.severity}</Pill>
                    {v.cve_id&&<span style={{ fontFamily:"'DM Mono',monospace",fontSize:11,color:C.accent }}>{v.cve_id}</span>}
                    {v.status==="patched"&&<Pill color={C.accent}>Patched</Pill>}
                    {v.status==="accepted_risk"&&<Pill color={C.gold}>Accepted</Pill>}
                  </div>
                  <div style={{ fontWeight:700,fontSize:mob?13:15 }}>{v.name}</div>
                </div>
                <div style={{ textAlign:"right",flexShrink:0 }}>
                  {v.cvss_score&&<><div style={{ fontFamily:"'DM Mono',monospace",fontSize:mob?22:26,fontWeight:700,color:sevCol(v.severity),lineHeight:1 }}>{v.cvss_score}</div><div style={{ fontSize:10,color:C.textMuted }}>CVSS</div></>}
                </div>
              </div>
              <div style={{ display:"flex",gap:12,fontSize:11,flexWrap:"wrap",marginBottom:8 }}>
                {v.target_name&&<span style={{ color:C.textMuted }}>Target: <span style={{ color:C.accent,fontFamily:"'DM Mono',monospace" }}>{v.target_name}</span></span>}
                {v.port&&<span style={{ color:C.textMuted }}>Port: <span style={{ color:C.accent,fontFamily:"'DM Mono',monospace" }}>{v.port}</span></span>}
              </div>
              {v.status==="open" && (
                <div style={{ display:"flex",gap:6 }}>
                  <Btn size="sm" variant="outline" onClick={()=>updateStatus(v.id,"patched")}>Mark Patched</Btn>
                  <Btn size="sm" variant="ghost" onClick={()=>updateStatus(v.id,"accepted_risk")}>Accept Risk</Btn>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Reports ────────────────────────────────────────────────
function Reports({ bp }) {
  const mob = isMob(bp);
  const [scans,      setScans]      = useState([]);
  const [scanId,     setScanId]     = useState("");
  const [reports,    setReports]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadReports = useCallback(() => {
    api("/api/reports").then(d => setReports(Array.isArray(d)?d:[]));
  }, []);

  useEffect(() => {
    Promise.all([api("/api/scans"), api("/api/reports")]).then(([sc, rp]) => {
      const s = Array.isArray(sc)?sc:[];
      const r = Array.isArray(rp)?rp:[];
      setScans(s.filter(x=>x.status==="completed"));
      setReports(r);
      if (s.length > 0) setScanId(s.find(x=>x.status==="completed")?.id || "");
      setLoading(false);
    });
  }, []);

  // Poll every 5 s while any report is still generating
  useEffect(() => {
    const hasPending = reports.some(r => r.status === "pending" || r.status === "generating");
    if (!hasPending) return;
    const iv = setInterval(loadReports, 5000);
    return () => clearInterval(iv);
  }, [reports, loadReports]);

  const generate = async (type) => {
    if (!scanId) return;
    setGenerating(true);
    const res = await api("/api/reports", { method:"POST", body: JSON.stringify({ scan_id: scanId, report_type: type, format:"pdf" }) });
    if (res.id) setReports(p=>[res,...p]);
    setGenerating(false);
  };

  const download = (reportId) => {
    const token = localStorage.getItem("sentinel_token");
    const a = document.createElement("a");
    a.href = `${API_URL}/api/reports/${reportId}/download`;
    a.setAttribute("download", `sentinel-report-${reportId}.pdf`);
    // Fetch with auth header and create object URL
    fetch(a.href, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  if (loading) return <Spinner />;

  return (
    <div className="fade">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Reports</h1>
        <div style={{ fontSize:12,color:C.textMuted }}>Generate and download security reports</div>
      </div>

      {scans.length > 0 && (
        <Card style={{ padding:mob?14:18,marginBottom:16 }}>
          <div style={{ fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:8 }}>Select Scan</div>
          <select value={scanId} onChange={e=>setScanId(e.target.value)} style={{ marginBottom:0 }}>
            {scans.map(s=><option key={s.id} value={s.id}>{s.target_name} — {s.scan_type} — {new Date(s.created_at).toLocaleDateString()}</option>)}
          </select>
        </Card>
      )}

      <div style={{ display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:12,marginBottom:20 }}>
        {[
          {type:"executive",  title:"Executive Summary",  desc:"Risk overview for management.",      icon:"⊟",color:C.accent},
          {type:"technical",  title:"Technical Report",   desc:"Full CVE list and port analysis.",   icon:"⊞",color:C.warn},
          {type:"remediation",title:"Remediation Guide",  desc:"Prioritised fix steps per finding.", icon:"⊕",color:C.gold},
          {type:"vulnerability_matrix",title:"Vuln Matrix",desc:"Spreadsheet-ready breakdown.",     icon:"▦",color:"#5b9bd5"},
        ].map(r=>(
          <Card key={r.type} style={{ padding:mob?14:20 }}>
            <div style={{ fontSize:mob?22:26,marginBottom:10,color:r.color }}>{r.icon}</div>
            <div style={{ fontWeight:700,fontSize:mob?14:16,marginBottom:6 }}>{r.title}</div>
            <div style={{ fontSize:12,color:C.textDim,marginBottom:14,lineHeight:1.6 }}>{r.desc}</div>
            <Btn variant="ghost" size="sm" style={{ borderColor:r.color+"44",color:r.color }} onClick={()=>generate(r.type)} loading={generating} disabled={!scanId||scans.length===0}>
              Generate PDF
            </Btn>
          </Card>
        ))}
      </div>

      {reports.length > 0 && (
        <Card>
          <div style={{ padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontWeight:700,fontSize:14 }}>Generated Reports</div>
          {reports.map((r,i)=>(
            <div key={r.id||i} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:`1px solid ${C.border}22`,gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600,marginBottom:2,textTransform:"capitalize",fontSize:13 }}>{r.report_type?.replace(/_/g," ")||"Report"}</div>
                <div style={{ fontSize:11,color:C.textMuted }}>{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <Pill color={r.status==="ready"?C.accent:r.status==="failed"?C.critical:C.gold}>
                  {r.status==="generating"?"building...":r.status}
                </Pill>
                {r.status==="ready" && (
                  <Btn variant="outline" size="sm" onClick={()=>download(r.id)}>↓ PDF</Btn>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      {scans.length === 0 && (
        <Card style={{ padding:40,textAlign:"center" }}>
          <div style={{ fontSize:13,color:C.textMuted }}>Run a scan first to generate reports.</div>
        </Card>
      )}
    </div>
  );
}

// ── Billing ────────────────────────────────────────────────
function Billing({ bp }) {
  const mob = isMob(bp);
  const [sub,        setSub]        = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [subLoading, setSubLoading] = useState(null);

  useEffect(() => {
    api("/api/subscription").then(d => { setSub(d); setLoading(false); });
  }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription?")) return;
    setCancelling(true);
    await api("/api/subscription", { method:"DELETE" });
    setSub(null);
    setCancelling(false);
  };

  const subscribe = async (planSlug) => {
    setSubLoading(planSlug);
    try {
      const data = await api("/api/payfast/initiate", { method:"POST", body: JSON.stringify({ plan_slug: planSlug }) });
      if (data.url && data.fields) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.url;
        Object.entries(data.fields).forEach(([k, v]) => {
          const inp = document.createElement("input");
          inp.type = "hidden"; inp.name = k; inp.value = v;
          form.appendChild(inp);
        });
        document.body.appendChild(form);
        form.submit();
      } else {
        alert(data.error || "Failed to initiate payment. Please try again.");
      }
    } catch(e) {
      alert("Could not connect to payment service.");
    }
    setSubLoading(null);
  };

  if (loading) return <Spinner />;

  return (
    <div className="fade">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Billing</h1>
        <div style={{ fontSize:12,color:C.textMuted }}>Manage your subscription</div>
      </div>

      {sub ? (
        <Card style={{ padding:mob?14:20,marginBottom:14,border:`1px solid ${C.accent}40` }}>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:10,color:C.textMuted,letterSpacing:1,marginBottom:6 }}>CURRENT PLAN</div>
          <div style={{ fontWeight:900,fontSize:mob?24:32,color:C.accent,marginBottom:6 }}>{sub.plan_name} Plan</div>
          <div style={{ fontSize:mob?32:44,fontWeight:900,fontFamily:"'DM Mono',monospace",marginBottom:4 }}>R{parseFloat(sub.amount).toFixed(0)}</div>
          <div style={{ fontSize:12,color:C.textMuted,marginBottom:16 }}>per month{sub.current_period_end?` · renews ${new Date(sub.current_period_end).toLocaleDateString()}`:""}</div>
          <Btn variant="danger" size={mob?"sm":"md"} onClick={cancel} loading={cancelling}>Cancel Subscription</Btn>
        </Card>
      ) : (
        <Card style={{ padding:mob?14:20,marginBottom:14 }}>
          <div style={{ fontSize:36,marginBottom:12,textAlign:"center" }}>◈</div>
          <div style={{ fontWeight:700,fontSize:16,marginBottom:8,textAlign:"center" }}>No Active Subscription</div>
          <div style={{ fontSize:13,color:C.textMuted,marginBottom:20,textAlign:"center" }}>You're on the free trial. Subscribe to unlock full features.</div>
          <div style={{ display:"grid",gridTemplateColumns:mob?"1fr":"repeat(3,1fr)",gap:12 }}>
            {[
              {name:"Starter",    slug:"starter",    price:"499",   targets:"5 targets",  scans:"10 scans/mo"},
              {name:"Business",   slug:"business",   price:"1,499", targets:"25 targets", scans:"50 scans/mo"},
              {name:"Enterprise", slug:"enterprise", price:"4,999", targets:"Unlimited",  scans:"Unlimited"},
            ].map(p=>(
              <Card key={p.slug} style={{ padding:"16px 18px",textAlign:"left",border:`1px solid ${p.slug==="business"?C.accent:C.border}` }}>
                <div style={{ fontWeight:700,marginBottom:4 }}>{p.name}</div>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:20,fontWeight:700,color:C.accent,marginBottom:4 }}>R{p.price}<span style={{ fontSize:11,color:C.textMuted,fontFamily:"sans-serif",fontWeight:400 }}>/mo</span></div>
                <div style={{ fontSize:11,color:C.textMuted,marginBottom:10 }}>{p.targets} · {p.scans}</div>
                <Btn variant="primary" size="sm" full loading={subLoading===p.slug} onClick={()=>subscribe(p.slug)}>
                  Subscribe via PayFast
                </Btn>
              </Card>
            ))}
          </div>
          <div style={{ marginTop:14,fontSize:11,color:C.textMuted,textAlign:"center" }}>
            Payments processed securely by PayFast · PCI DSS Level 1 · Billed in ZAR
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Settings ───────────────────────────────────────────────
function Settings({ bp, user }) {
  const mob = isMob(bp);
  const [form,     setForm]     = useState({ full_name: user?.full_name||"", company_name: user?.company_name||"", email: user?.email||"", phone: user?.phone||"" });
  const [saved,    setSaved]    = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [pwForm,   setPwForm]   = useState({ current_password:"", new_password:"", confirm_password:"" });
  const [pwSaved,  setPwSaved]  = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwErr,    setPwErr]    = useState("");

  const save = async () => {
    setSaving(true);
    await api("/api/auth/profile", { method:"PUT", body: JSON.stringify(form) });
    const stored = localStorage.getItem("sentinel_user");
    if (stored) {
      const u = JSON.parse(stored);
      localStorage.setItem("sentinel_user", JSON.stringify({ ...u, full_name: form.full_name, company_name: form.company_name }));
    }
    setSaving(false);
    setSaved(true);
    setTimeout(()=>setSaved(false), 2500);
  };

  const changePassword = async () => {
    setPwErr("");
    if (!pwForm.current_password || !pwForm.new_password) { setPwErr("All fields are required"); return; }
    if (pwForm.new_password.length < 8) { setPwErr("New password must be at least 8 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { setPwErr("Passwords do not match"); return; }
    setPwSaving(true);
    const res = await api("/api/auth/password", { method:"PUT", body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }) });
    setPwSaving(false);
    if (res.message) {
      setPwSaved(true);
      setPwForm({ current_password:"", new_password:"", confirm_password:"" });
      setTimeout(()=>setPwSaved(false), 3000);
    } else {
      setPwErr(res.error || "Password update failed");
    }
  };

  return (
    <div className="fade">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:mob?18:22,fontWeight:800,marginBottom:4 }}>Settings</h1>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:14 }}>
        <Card style={{ padding:mob?14:20 }}>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Profile</div>
          {[
            { label:"Full Name",    key:"full_name",    type:"text" },
            { label:"Company",      key:"company_name", type:"text" },
            { label:"Email",        key:"email",        type:"email" },
            { label:"Phone",        key:"phone",        type:"text" },
          ].map(f=>(
            <div key={f.key} style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>{f.label}</label>
              <input type={f.type} value={form[f.key]||""} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} disabled={f.key==="email"} style={{ opacity:f.key==="email"?.6:1 }} />
            </div>
          ))}
          <Btn variant="primary" full onClick={save} loading={saving}>
            {saved?"✓ Saved":"Save Changes"}
          </Btn>
        </Card>
        <Card style={{ padding:mob?14:20 }}>
          <div style={{ fontWeight:700,fontSize:14,marginBottom:16 }}>Change Password</div>
          {[
            { label:"Current Password", key:"current_password" },
            { label:"New Password",     key:"new_password"     },
            { label:"Confirm Password", key:"confirm_password" },
          ].map(f=>(
            <div key={f.key} style={{ marginBottom:12 }}>
              <label style={{ display:"block",fontSize:11,color:C.textMuted,fontFamily:"'DM Mono',monospace",letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>{f.label}</label>
              <input type="password" placeholder="••••••••" value={pwForm[f.key]} onChange={e=>setPwForm(p=>({...p,[f.key]:e.target.value}))} />
            </div>
          ))}
          {pwErr   && <div style={{ fontSize:12,color:C.critical,marginBottom:10 }}>⚠ {pwErr}</div>}
          {pwSaved && <div style={{ fontSize:12,color:C.accent,marginBottom:10 }}>✓ Password updated successfully</div>}
          <Btn variant="outline" full onClick={changePassword} loading={pwSaving}>Update Password</Btn>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  ROOT
// ═══════════════════════════════════════════════════════════
export default function ResponsiveApp({ user, onLogout }) {
  const bp              = useBreakpoint();
  const [page, setPage] = useState("dashboard");
  const [drawer, setDrawer] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const mob = isMob(bp);
  const tab = bp === "tablet";

  useEffect(() => { setSidebarCollapsed(tab); }, [tab]);

  const sideW = mob ? 0 : sidebarCollapsed ? 64 : 220;
  const topH  = mob ? 56 : 0;
  const botH  = mob ? 60 : 0;

  const pageProps = { bp, onNav: setPage, user, onLogout };

  return (
    <>
      <style>{CSS}</style>

      {mob && <MobileHeader page={page} user={user} onMenuOpen={() => setDrawer(true)} />}
      {mob && <MobileDrawer open={drawer} onClose={() => setDrawer(false)} page={page} onNav={setPage} user={user} onLogout={onLogout} />}

      {!mob && (
        <>
          <Sidebar page={page} onNav={setPage} user={user} onLogout={onLogout} collapsed={sidebarCollapsed} />
          <button onClick={() => setSidebarCollapsed(p => !p)} style={{ position:"fixed",top:"50%",left:sideW-11,transform:"translateY(-50%)",width:22,height:22,borderRadius:"50%",background:C.surface,border:`1px solid ${C.border}`,color:C.textMuted,cursor:"pointer",fontSize:11,zIndex:25,transition:"left .25s ease",display:"flex",alignItems:"center",justifyContent:"center" }}>
            {sidebarCollapsed ? "›" : "‹"}
          </button>
        </>
      )}

      <div style={{ marginLeft:sideW,paddingTop:topH,paddingBottom:botH,minHeight:"100vh",transition:"margin-left .25s ease" }}>
        <div style={{ padding:mob?"16px 14px 20px":"28px 28px" }}>
          {page === "dashboard"       && <Dashboard       {...pageProps} />}
          {page === "scanner"         && <Scanner          {...pageProps} />}
          {page === "targets"         && <Targets          {...pageProps} />}
          {page === "vulnerabilities" && <Vulns            {...pageProps} />}
          {page === "reports"         && <Reports          {...pageProps} />}
          {page === "billing"         && <Billing          {...pageProps} />}
          {page === "settings"        && <Settings         {...pageProps} />}
        </div>
      </div>

      {mob && <BottomNav page={page} onNav={setPage} />}
    </>
  );
}
